import type { ColourCount, VehicleFact, VehicleRarity } from '../api/types'
import { formatColour, formatNumber } from './format'
import { SIDECODE_COUNT, sidecodePattern } from './licensePlate'

/** One line of the colour breakdown, ready to render. */
export interface ColourRow {
  label: string
  count: number
  sharePercent: number
  /** Whether this is the colour the vehicle in the report is registered in. */
  isThisCar: boolean
  /** Whether this row sums the colours that did not make the list, rather than being one colour. */
  isOther: boolean
}

/** How many colours are worth showing before the tail becomes noise. */
const DEFAULT_LIMIT = 8

function sameColour(a: string | undefined, b: string | undefined): boolean {
  return a !== undefined && b !== undefined && a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Reduces the full colour histogram to a readable list.
 *
 * A popular model is registered in thirty-odd colours with a long tail of one-offs, so only the
 * head is shown. Two rules keep that from lying:
 *
 * - **This car's own colour is always in the list**, even when it is the twentieth most common —
 *   that row is the entire reason a reader looks at this section, and dropping it for being rare
 *   would hide the most interesting answer.
 * - **The tail is summed rather than discarded**, so the percentages still account for the whole
 *   population instead of quietly stopping at 74%.
 */
export function summariseColours(rarity: VehicleRarity, limit = DEFAULT_LIMIT): ColourRow[] {
  const colours = rarity.colours ?? []

  if (colours.length === 0) {
    return []
  }

  const shown = colours.slice(0, limit)
  const own = colours.find((colour) => sameColour(colour.colour, rarity.colour))

  if (own && !shown.includes(own)) {
    shown.push(own)
  }

  const rows = shown.map((colour) => toRow(colour, rarity))
  const rest = colours.filter((colour) => !shown.includes(colour))

  if (rest.length > 0) {
    rows.push({
      label: `Overige kleuren (${rest.length})`,
      count: rest.reduce((total, colour) => total + colour.count, 0),
      sharePercent: rest.reduce((total, colour) => total + colour.sharePercent, 0),
      isThisCar: false,
      isOther: true,
    })
  }

  return rows
}

function toRow(colour: ColourCount, rarity: VehicleRarity): ColourRow {
  return {
    label: formatColour(colour.colour) ?? colour.colour,
    count: colour.count,
    sharePercent: colour.sharePercent,
    isThisCar: sameColour(colour.colour, rarity.colour),
    isOther: false,
  }
}

/**
 * The share of this model that shares this car's colour, as a percentage, or `undefined` when
 * either figure is missing.
 *
 * Computed here rather than taken from the histogram because the RDW can report a colour for the
 * vehicle that does not appear in the model's own breakdown at all, in which case the honest answer
 * is zero — and `registeredInThisColour` already says so.
 */
export function colourShare(rarity: VehicleRarity): number | undefined {
  const { registeredInNetherlands, registeredInThisColour } = rarity

  if (!registeredInNetherlands || registeredInThisColour === undefined) {
    return undefined
  }

  return Math.round((registeredInThisColour / registeredInNetherlands) * 1000) / 10
}

/**
 * The "wist je dat" lines, phrased here because the API deliberately sends facts rather than
 * sentences — a kind and its numbers, with the Dutch left to whoever owns the rest of the labels.
 *
 * **Two of the four kinds are dropped, and that is the point.** `ModelPopulation` and
 * `ColourPopulation` are exactly what the lead sentence of this card already says, in the same
 * words and about the same car. Repeating them a few centimetres lower is how a section that is
 * supposed to reward reading turns into one people scroll past — which is the same reasoning the
 * API applies when it declines to emit a fact at all. What is left is what the histogram above does
 * not already state: the rarest colour, and the plate's own layout.
 *
 * An empty result is ordinary and renders nothing.
 */
export function summariseFacts(facts: VehicleFact[] | undefined): string[] {
  return (facts ?? [])
    .map(phrase)
    .filter((sentence): sentence is string => sentence !== undefined)
}

function phrase(fact: VehicleFact): string | undefined {
  switch (fact.kind) {
    case 'RarestColour': {
      const colour = formatColour(fact.value)?.toLowerCase()
      const count = formatNumber(fact.count)

      if (!colour || fact.count === undefined) {
        return undefined
      }

      return fact.count === 1
        ? `Deze auto is de enige van dit model in de kleur ${colour}: geen enkele andere is zo gespoten.`
        : `${colour.charAt(0).toUpperCase()}${colour.slice(1)} is de zeldzaamste kleur waarin dit model rondrijdt: er zijn er maar ${count}.`
    }

    case 'PlateSidecode': {
      const pattern = sidecodePattern(fact.count)

      return pattern === undefined
        ? undefined
        : `Dit kenteken heeft sidecode ${fact.count} (${pattern}), één van de ${SIDECODE_COUNT} indelingen die de RDW ooit heeft uitgegeven.`
    }

    // Both are already the first sentence of this card. See the note above.
    case 'ModelPopulation':
    case 'ColourPopulation':
    default:
      return undefined
  }
}
